import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PromoCard } from '@/components/promo/PromoCard.js';
import { ActiveBonusHero } from '@/components/promo/ActiveBonusHero.js';
import { PromoTutorial } from '@/components/promo/PromoTutorial.js';
import { Coachmark } from '@/components/promo/Coachmark.js';
import { BonusSectionHeader } from '@/components/promo/BonusSectionHeader.js';
import {
  fetchPromotions,
  fetchMyClaims,
  claimPromotionV2,
  hasInProgressClaim,
  invalidateClaimState,
  PROMO_QUERY_KEYS,
  bigIntish,
  type Promotion,
  type PromotionClaim,
} from '@/features/promo/api.js';
import { WALLET_QUERY_KEYS } from '@/features/wallet/api.js';
import {
  bucketOf,
  blocksSingleGrantReclaim,
  isHistoricalClaim,
} from '@/features/promo/claim-buckets.js';
import { isPromoStillOffered, promoGroupOf, type PromoGroup } from '@/features/promo/list-visibility.js';
import {
  hasSeenTutorial,
  hasSeenCoachmark,
} from '@/features/promo/tutorial-state.js';
import {
  claimFailureNotice,
  refusalOutdatesRebatePreview,
} from '@/features/promo/claim-errors.js';
import { useFormatMoney, useMoneyCurrency } from '@/components/money/MoneyProvider.js';

interface PromoOffersSectionProps {
  /** Which kinds this tab lists — see `promoGroupOf`. */
  group: PromoGroup;
}

/**
 * The claimable promotion cards of one bonus-hub tab. The promotions tab also
 * carries the in-progress bonuses and the first-visit tutorial; check-in and
 * cashback list only their own cards.
 */
export function PromoOffersSection({ group }: PromoOffersSectionProps) {
  const { t } = useTranslation('promo');
  const qc = useQueryClient();
  const isPromotions = group === 'promotions';

  const [showTutorial, setShowTutorial] = useState(false);
  const [showCoachmark, setShowCoachmark] = useState(false);

  useEffect(() => {
    if (!isPromotions) return;
    if (!hasSeenTutorial()) setShowTutorial(true);
    else if (!hasSeenCoachmark()) setShowCoachmark(true);
  }, [isPromotions]);

  // Hot query: only the in-progress bonuses (small payload). Always refetch on
  // navigation back to /promos — switching from an in-SPA game route is a mount,
  // not a window focus, so the global refetchOnWindowFocus doesn't fire. While a
  // bonus is still working off turnover, poll on a bounded interval (visible tab
  // only) so the async turnover update lands without a manual refresh; the poll
  // stops the moment no in-progress claim remains.
  const activeClaimsQuery = useQuery({
    queryKey: PROMO_QUERY_KEYS.myClaims({ status: 'active' }),
    queryFn: () => fetchMyClaims({ status: 'active' }),
    staleTime: 10_000,
    refetchOnMount: 'always',
    refetchInterval: (q) => (hasInProgressClaim(q.state.data?.items) ? 7000 : false),
  });

  const currency = useMoneyCurrency();
  const formatMoney = useFormatMoney();

  const promosQuery = useQuery({
    queryKey: PROMO_QUERY_KEYS.list({ status: 'active' }),
    queryFn: () => fetchPromotions({ status: 'active' }),
    staleTime: 60_000,
  });

  // Cold query: a bounded recent window across all statuses, used only to pin a
  // converted welcome/first_deposit claim to its card (the "withdraw" CTA). It
  // changes rarely, so normal staleTime + no poll.
  //
  // 200 (the server's hard cap) rather than a smaller window, and it must match
  // the deposit screen's: the two share this cache key, so whichever mounts
  // first decides the window both read. Two limits behind one key is one screen
  // silently getting the other's page size.
  const recentClaimsQuery = useQuery({
    queryKey: PROMO_QUERY_KEYS.myClaims({ status: 'recent' }),
    queryFn: () => fetchMyClaims({ limit: 200 }),
    staleTime: 60_000,
  });

  const claimMutation = useMutation({
    mutationFn: (promo: Promotion) => claimPromotionV2({ promotionId: promo.id }),
    onSuccess: (claim: PromotionClaim) => {
      invalidateClaimState(qc);
      void qc.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balances });
      // Rebate cards read claimable state from the preview cache; refresh it so a
      // just-claimed period stops offering a claim — and the figure moves on to
      // whichever period the server now points at — without waiting out the 60s
      // staleTime.
      // The whole family, not the promotion that was claimed: taking the play
      // rebate spends the period for the loss cashback and vice versa, so the
      // sibling card is offering a claim the server will now refuse (#161).
      void qc.invalidateQueries({ queryKey: PROMO_QUERY_KEYS.rebatePreviewRoot });
      const minor = bigIntish(claim.grantedAmount ?? claim.amount);
      // A 200 here is not proof that money moved. The claim endpoint hands back
      // the row the member already holds when the same tap repeats, and that row
      // can be one that already ended — forfeited at expiry, revoked, completed.
      // It still carries its original `grantedAmount`, so announcing the amount
      // would tell the player they just received a bonus that was taken off them.
      if (isHistoricalClaim(claim.status)) {
        toast.info(t('toast.alreadyClaimed'));
      } else if (minor > 0n) {
        toast.success(t('toast.claimSuccess', { amount: formatMoney(minor, claim.currency ?? currency) }));
      } else {
        toast.success(t('toast.claimReceived'));
      }
    },
    onError: (err) => {
      // The card that was clicked offered a claim the server had already given
      // to the sibling rebate: its preview predates that claim, and so does the
      // sibling's. Refresh both so the button goes disabled under the caption
      // that explains why.
      if (refusalOutdatesRebatePreview(err)) {
        void qc.invalidateQueries({ queryKey: PROMO_QUERY_KEYS.rebatePreviewRoot });
      }
      const notice = claimFailureNotice(err, t);
      if (notice.tone === 'info') toast.info(notice.message);
      else toast.error(notice.message);
    },
  });

  const promos = promosQuery.data?.items ?? [];
  const activeClaims = (activeClaimsQuery.data?.items ?? []).filter(
    (c) => bucketOf(c.status) === 'active',
  );
  const recentClaims = recentClaimsQuery.data?.items ?? [];
  const claimByPromoId = new Map(activeClaims.map((c) => [c.promotionId, c]));
  // Single-grant bonuses (welcome / first_deposit) can't be re-claimed once a
  // claim exists — including a pending reservation still awaiting a deposit,
  // or one converted to cash after turnover completes. activeClaimsQuery only
  // ever returns status='active' rows, so pin any other non-rejected claim
  // from the broader recent window here — the card then shows the correct
  // "go deposit" / "wager" / "withdraw" CTA rather than reverting to "claim".
  for (const p of promos) {
    if (p.kind !== 'welcome' && p.kind !== 'first_deposit') continue;
    if (claimByPromoId.has(p.id)) continue;
    const existing = recentClaims.find(
      (c) => c.promotionId === p.id && blocksSingleGrantReclaim(c.status),
    );
    if (existing) claimByPromoId.set(p.id, existing);
  }

  // This section lists what the player can still take up. A promotion whose
  // claim has run its course, and a promotion past its end date, are both
  // offers that no longer exist — they belong to the history screen, not here.
  const offered = promos.filter(
    (p) => promoGroupOf(p.kind) === group && isPromoStillOffered(p, claimByPromoId.get(p.id) ?? null),
  );

  const heading = {
    promotions: { title: t('title'), subtitle: t('subtitle') },
    checkin: { title: t('hub.tabs.checkin'), subtitle: t('hub.checkin.subtitle') },
    cashback: { title: t('hub.tabs.cashback'), subtitle: t('hub.cashback.subtitle') },
  }[group];

  const cards = promosQuery.isPending ? (
    <div className="grid gap-3 md:grid-cols-2 auto-rows-fr">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-32 rounded-2xl bg-[var(--bg-elevated)]/50 animate-pulse" />
      ))}
    </div>
  ) : offered.length === 0 ? (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-raised)] py-10 text-center text-[var(--text-muted)] text-[13px]">
      {t('active.empty')}
    </div>
  ) : (
    <>
      <div className="grid gap-3 md:grid-cols-2 auto-rows-fr">
        {offered.map((p) => (
          <PromoCard
            key={p.id}
            promo={p}
            claim={claimByPromoId.get(p.id) ?? null}
            currency={currency}
            claiming={claimMutation.isPending && claimMutation.variables?.id === p.id}
            onClaim={(promo) => claimMutation.mutate(promo)}
          />
        ))}
      </div>
      {showCoachmark && <Coachmark open onClose={() => setShowCoachmark(false)} />}
    </>
  );

  return (
    <div className="flex flex-col gap-5">
      <BonusSectionHeader
        title={heading.title}
        subtitle={heading.subtitle}
        onHowTo={isPromotions ? () => setShowTutorial(true) : undefined}
        historyTab={group}
      />

      {isPromotions && (
        <section>
          <ActiveBonusHero claims={activeClaims} currency={currency} />
        </section>
      )}

      {isPromotions ? (
        <section className="flex flex-col gap-3">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-[13px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
              {t('active.title')}
            </h3>
            <span className="text-[11px] text-[var(--text-muted)]">
              {t('active.subtitle')}
            </span>
          </div>
          {cards}
        </section>
      ) : (
        <section>{cards}</section>
      )}

      {isPromotions && <PromoTutorial open={showTutorial} onClose={() => setShowTutorial(false)} />}
    </div>
  );
}
